import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ChevronDown, ChevronRight, Download, TrendingUp } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  descargarNominacionDartXlsx,
  getNominacionDart,
  type DartDia,
  type DartFiltro,
  type DartHora,
} from "@/lib/api";

/**
 * Operación → Contrapartes → DART del arbitraje.
 *
 * Cuánto se ganó o perdió con el ARBITRAJE de las nominaciones guardadas en
 * /nominacion, por día-hora y nodo/zona. El precio de cada centro de carga es el
 * de Configuración → Centros de carga (PML del nodo o PND de la zona, MXN/MWh).
 *   Arbitraje > 0 = compra en MDA; < 0 = venta en MDA.
 *   DART = MDA − MTR;  P&L = arbitraje × (MTR − MDA)  (positivo = ganancia).
 * Las horas sin MTR publicado quedan sin P&L.
 */

const COLOR_POS = "#384D3A";   // olivo MPP
const COLOR_NEG = "#B4533A";
const COLOR_LINEA = "#6B7F6D";

const nf = (dec: number) => new Intl.NumberFormat("es-MX", {
  minimumFractionDigits: dec, maximumFractionDigits: dec,
});
const num = (n: number | null | undefined, dec = 2) =>
  n == null || Number.isNaN(n) ? "—" : nf(dec).format(n);
const mxn = (n: number | null | undefined) =>
  n == null ? "—" : `${n < 0 ? "−" : ""}$${nf(0).format(Math.abs(n))}`;
const compacto = (v: number) =>
  Math.abs(v) >= 1e6 ? `${(v / 1e6).toFixed(1)} M` : Math.abs(v) >= 1e3 ? `${(v / 1e3).toFixed(0)} k` : `${v}`;

const fechaCorta = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("es-MX", {
    weekday: "short", day: "2-digit", month: "short", year: "numeric",
  });
const fechaDM = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("es-MX", { day: "2-digit", month: "short" });

function hoyMas(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const claveDia = (d: DartDia) => `${d.fecha}|${d.id_contraparte}|${d.centro_carga}`;
const signo = (n: number | null | undefined) =>
  n == null ? "text-slate-400" : n < 0 ? "text-red-700" : "text-emerald-800";

export function NominacionDartPage() {
  const [desde, setDesde] = useState(hoyMas(-30));
  const [hasta, setHasta] = useState(hoyMas(0));
  const [contraparte, setContraparte] = useState<number | "">("");
  const [abierto, setAbierto] = useState<string | null>(null);
  const [descargando, setDescargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filtro: DartFiltro = { desde, hasta, id_contraparte: contraparte };
  const q = useQuery({
    queryKey: ["nominacion-dart", desde, hasta, contraparte],
    queryFn: () => getNominacionDart(filtro),
  });
  const data = q.data;

  // Contrapartes disponibles (del resultado sin filtrar por contraparte, para
  // poder cambiar de una a otra sin perder el combo).
  const qTodas = useQuery({
    queryKey: ["nominacion-dart", desde, hasta, ""],
    queryFn: () => getNominacionDart({ desde, hasta, id_contraparte: "" }),
    enabled: contraparte !== "",
  });
  const contrapartes = useMemo(() => {
    const src = (contraparte === "" ? data : qTodas.data)?.dias ?? [];
    const m = new Map<number, string>();
    src.forEach((d) => m.set(d.id_contraparte, d.alias || d.contraparte));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [data, qTodas.data, contraparte]);

  // Gráfica: una barra por día (suma de los centros de carga) + P&L acumulado.
  const grafica = useMemo(() => {
    if (!data) return [];
    const porDia = new Map<string, { pl: number; conMtr: boolean }>();
    data.dias.forEach((d) => {
      const g = porDia.get(d.fecha) ?? { pl: 0, conMtr: false };
      if (d.pl_mxn != null) { g.pl += d.pl_mxn; g.conMtr = true; }
      porDia.set(d.fecha, g);
    });
    let acum = 0;
    return [...porDia.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([fecha, g]) => {
      acum += g.pl;
      return { fecha, etiqueta: fechaDM(fecha), pl: g.conMtr ? g.pl : null, acumulado: acum, conMtr: g.conMtr };
    });
  }, [data]);

  const horasDe = (d: DartDia): DartHora[] =>
    (data?.horas ?? []).filter((h) =>
      h.fecha === d.fecha && h.id_contraparte === d.id_contraparte && h.centro_carga === d.centro_carga);

  async function descargar() {
    setError(null);
    setDescargando(true);
    try {
      await descargarNominacionDartXlsx(filtro);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDescargando(false);
    }
  }

  const t = data?.totales;
  const pctGana = t && t.horas_con_mtr ? (t.horas_ganancia / t.horas_con_mtr) * 100 : null;
  const sinMtr = t ? t.horas - t.horas_con_mtr : 0;
  const sinNodo = data?.dias.some((d) => d.sin_nodo) ?? false;

  return (
    <div className="space-y-4 max-w-7xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-slate-500" /> DART del arbitraje
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Cuánto se ganó o perdió con el <b>arbitraje</b> de las nominaciones guardadas en{" "}
            <Link to="/nominacion" className="text-blue-600 hover:underline">Nominación a contraparte</Link>,
            por día-hora y nodo. Precio = nodo o zona de{" "}
            <Link to="/arbitraje/centros-carga" className="text-blue-600 hover:underline">Centros de carga</Link>.
            P&amp;L = arbitraje × (MTR − MDA); arbitraje negativo = venta en MDA, positivo = compra.
          </p>
        </div>
        <button
          type="button"
          onClick={descargar}
          disabled={descargando || !data || data.horas.length === 0}
          className="text-sm inline-flex items-center gap-1 px-3 py-1.5 border rounded bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-50"
          title="Resumen por día, por nodo y detalle por hora en Excel"
        >
          <Download className="w-4 h-4" /> {descargando ? "Generando…" : "Descargar Excel"}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{error}</div>
      )}

      <div className="bg-white border rounded-lg p-3 flex flex-wrap items-end gap-3 text-sm">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-500">Contraparte</span>
          <select
            value={contraparte}
            onChange={(e) => setContraparte(e.target.value === "" ? "" : Number(e.target.value))}
            className="border rounded px-2 py-1 bg-white min-w-48"
          >
            <option value="">Todas</option>
            {contrapartes.map(([id, nombre]) => (
              <option key={id} value={id}>{nombre}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-500">Operación desde</span>
          <input type="date" value={desde} max={hasta || undefined}
            onChange={(e) => setDesde(e.target.value)} className="border rounded px-2 py-1" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-slate-500">hasta</span>
          <input type="date" value={hasta} min={desde || undefined}
            onChange={(e) => setHasta(e.target.value)} className="border rounded px-2 py-1" />
        </label>
        <button
          type="button"
          onClick={() => { setDesde(hoyMas(-30)); setHasta(hoyMas(0)); setContraparte(""); }}
          className="px-3 py-1 border rounded bg-white hover:bg-slate-50 text-slate-600"
          title="Volver a los últimos 30 días y todas las contrapartes"
        >
          Restablecer
        </button>
      </div>

      {q.isLoading && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">Cargando…</div>
      )}
      {q.isError && (
        <div className="bg-white border rounded-lg p-8 text-center text-red-600 text-sm">
          {(q.error as Error).message}
        </div>
      )}
      {data && data.horas.length === 0 && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">
          No hay nominaciones guardadas en el rango y contraparte elegidos.
        </div>
      )}

      {data && t && data.horas.length > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi titulo="P&L realizado" valor={mxn(t.pl_mxn)} clase={signo(t.pl_mxn)}
              nota={`${t.horas_con_mtr} h con MTR`} />
            <Kpi titulo="Arbitraje nominado" valor={`${num(t.arbitraje_mwh, 1)} MWh`}
              nota={t.arbitraje_mwh < 0 ? "neto: venta en MDA" : "neto: compra en MDA"} />
            <Kpi titulo="Horas con ganancia" valor={pctGana == null ? "—" : `${num(pctGana, 0)} %`}
              nota={`${t.horas_ganancia} de ${t.horas_con_mtr}`} />
            <Kpi titulo="P&L medio por hora"
              valor={t.horas_con_mtr ? mxn(t.pl_mxn / t.horas_con_mtr) : "—"}
              clase={signo(t.horas_con_mtr ? t.pl_mxn : null)} />
            <Kpi titulo="Horas sin MTR" valor={String(sinMtr)}
              clase={sinMtr ? "text-amber-700" : undefined}
              nota={data.mtr_hasta.map((m) => `${m.punto.split(" ")[0]}: ${m.fecha ? fechaDM(m.fecha) : "—"}`).join(" · ")} />
          </div>

          {(sinMtr > 0 || sinNodo) && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm flex gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                {sinMtr > 0 && (
                  <div>
                    {sinMtr} horas todavía no tienen MTR publicado (
                    {data.mtr_hasta.map((m) => `${m.punto}: hasta ${m.fecha ? fechaCorta(m.fecha) : "sin precios"}`).join("; ")}
                    ). Quedan sin P&amp;L hasta que el ETL cargue el MTR.
                  </div>
                )}
                {sinNodo && (
                  <div>
                    Hay centros de carga sin nodo asignado: asígnalo en{" "}
                    <Link to="/arbitraje/centros-carga" className="underline">Centros de carga</Link> para valorizarlos.
                  </div>
                )}
              </div>
            </div>
          )}

          <section className="bg-white border rounded-lg p-3">
            <div className="text-sm font-medium text-slate-700 mb-2">
              P&amp;L por día (todos los centros de carga) y acumulado
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={grafica} margin={{ top: 8, right: 12, bottom: 4, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="etiqueta" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={compacto} width={62} />
                  <Tooltip
                    formatter={(v: number, n: string) => [v == null ? "sin MTR" : mxn(v), n]}
                    labelFormatter={(l: string) => {
                      const d = grafica.find((x) => x.etiqueta === l);
                      return d ? fechaCorta(d.fecha) + (d.conMtr ? "" : " · sin MTR") : l;
                    }}
                  />
                  <Legend />
                  <ReferenceLine y={0} stroke="#94a3b8" />
                  <Bar dataKey="pl" name="P&L del día (MXN)" radius={[3, 3, 0, 0]}>
                    {grafica.map((g) => (
                      <Cell key={g.fecha} fill={(g.pl ?? 0) < 0 ? COLOR_NEG : COLOR_POS} />
                    ))}
                  </Bar>
                  <Line type="monotone" dataKey="acumulado" name="Acumulado (MXN)"
                    stroke={COLOR_LINEA} strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="bg-white border rounded-lg overflow-x-auto">
            <div className="text-sm font-medium text-slate-700 px-3 pt-3">Por nodo / zona</div>
            <table className="w-full text-sm">
              <thead className="text-slate-600 text-left">
                <tr>
                  <th className="px-3 py-2">Nodo / zona</th>
                  <th className="px-3 py-2 text-right">Arbitraje MWh (con MTR)</th>
                  <th className="px-3 py-2 text-right">Horas con MTR</th>
                  <th className="px-3 py-2 text-right">Horas con ganancia</th>
                  <th className="px-3 py-2 text-right">P&amp;L medio / h</th>
                  <th className="px-3 py-2 text-right">P&amp;L MXN</th>
                </tr>
              </thead>
              <tbody>
                {data.nodos.map((n) => (
                  <tr key={n.punto} className="border-t">
                    <td className="px-3 py-1.5">{n.punto}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{num(n.arbitraje_mwh, 3)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{n.horas_con_mtr}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {n.horas_con_mtr ? `${num((n.horas_ganancia / n.horas_con_mtr) * 100, 0)} %` : "—"}
                    </td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${signo(n.pl_mxn)}`}>
                      {n.horas_con_mtr && n.pl_mxn != null ? mxn(n.pl_mxn / n.horas_con_mtr) : "—"}
                    </td>
                    <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${signo(n.pl_mxn)}`}>
                      {mxn(n.pl_mxn)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="bg-white border rounded-lg overflow-x-auto">
            <div className="text-sm font-medium text-slate-700 px-3 pt-3">
              Por día y centro de carga
              <span className="text-slate-400 font-normal"> · clic en un día para ver las 24 horas</span>
            </div>
            <table className="w-full text-sm">
              <thead className="text-slate-600 text-left">
                <tr>
                  <th className="px-2 py-2 w-6" />
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">Contraparte</th>
                  <th className="px-3 py-2">Centro de carga</th>
                  <th className="px-3 py-2">Nodo / zona</th>
                  <th className="px-3 py-2 text-right">Arbitraje MWh</th>
                  <th className="px-3 py-2 text-right">Horas con MTR</th>
                  <th className="px-3 py-2 text-right">Pérdidas</th>
                  <th className="px-3 py-2 text-right">Ganancias</th>
                  <th className="px-3 py-2 text-right">P&amp;L MXN</th>
                </tr>
              </thead>
              <tbody>
                {data.dias.map((d) => {
                  const k = claveDia(d);
                  const abiertoEste = abierto === k;
                  const conMtr = d.horas_con_mtr > 0;
                  return (
                    <Fragment key={k}>
                      <tr
                        className={`border-t cursor-pointer hover:bg-slate-50 ${abiertoEste ? "bg-slate-50" : ""}`}
                        onClick={() => setAbierto(abiertoEste ? null : k)}
                      >
                        <td className="px-2 py-1.5 text-slate-400">
                          {abiertoEste ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </td>
                        <td className="px-3 py-1.5 tabular-nums whitespace-nowrap">{fechaCorta(d.fecha)}</td>
                        <td className="px-3 py-1.5">{d.alias || d.contraparte}</td>
                        <td className="px-3 py-1.5">{d.centro_carga}</td>
                        <td className="px-3 py-1.5">
                          {d.sin_nodo ? <span className="text-amber-700">sin nodo</span> : d.punto}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{num(d.arbitraje_mwh, 3)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">
                          {d.horas_con_mtr}/{d.horas}
                          {!conMtr && <span className="ml-1 text-xs text-amber-700">sin MTR</span>}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-red-700">
                          {conMtr ? mxn(d.perdidas_mxn) : "—"}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-emerald-800">
                          {conMtr ? mxn(d.ganancias_mxn) : "—"}
                        </td>
                        <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${signo(d.pl_mxn)}`}>
                          {mxn(d.pl_mxn)}
                        </td>
                      </tr>
                      {abiertoEste && (
                        <tr className="border-t bg-slate-50/60">
                          <td colSpan={10} className="px-4 py-3">
                            <TablaHoras horas={horasDe(d)} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-100 font-medium">
                <tr>
                  <td colSpan={5} className="px-3 py-2">Total</td>
                  <td className="px-3 py-2 text-right tabular-nums">{num(t.arbitraje_mwh, 3)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{t.horas_con_mtr}/{t.horas}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-700">
                    {mxn(data.dias.reduce((s, d) => s + d.perdidas_mxn, 0))}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-800">
                    {mxn(data.dias.reduce((s, d) => s + d.ganancias_mxn, 0))}
                  </td>
                  <td className={`px-3 py-2 text-right tabular-nums ${signo(t.pl_mxn)}`}>{mxn(t.pl_mxn)}</td>
                </tr>
              </tfoot>
            </table>
          </section>
        </>
      )}
    </div>
  );
}

function Kpi({ titulo, valor, nota, clase }: {
  titulo: string; valor: string; nota?: string; clase?: string;
}) {
  return (
    <div className="bg-white border rounded-lg p-3">
      <div className="text-xs text-slate-500">{titulo}</div>
      <div className={`text-lg font-semibold tabular-nums ${clase ?? "text-slate-900"}`}>{valor}</div>
      {nota && <div className="text-xs text-slate-400 mt-0.5 truncate" title={nota}>{nota}</div>}
    </div>
  );
}

function TablaHoras({ horas }: { horas: DartHora[] }) {
  return (
    <table className="text-xs w-full max-w-4xl">
      <thead className="text-slate-500 text-left">
        <tr>
          <th className="px-2 py-1">Hora</th>
          <th className="px-2 py-1 text-right">Pronóstico MW</th>
          <th className="px-2 py-1 text-right">Arbitraje MW</th>
          <th className="px-2 py-1 text-right">MDA MXN/MWh</th>
          <th className="px-2 py-1 text-right">MTR MXN/MWh</th>
          <th className="px-2 py-1 text-right">DART (MDA − MTR)</th>
          <th className="px-2 py-1 text-right">P&amp;L MXN</th>
        </tr>
      </thead>
      <tbody>
        {horas.map((h) => (
          <tr key={h.hora} className="border-t border-slate-200">
            <td className="px-2 py-0.5 tabular-nums">{h.hora}</td>
            <td className="px-2 py-0.5 text-right tabular-nums">{num(h.pronostico_mw, 3)}</td>
            <td className={`px-2 py-0.5 text-right tabular-nums ${h.arbitraje_mw < 0 ? "text-red-700" : ""}`}>
              {num(h.arbitraje_mw, 3)}
            </td>
            <td className="px-2 py-0.5 text-right tabular-nums">{num(h.mda)}</td>
            <td className="px-2 py-0.5 text-right tabular-nums">{num(h.mtr)}</td>
            <td className={`px-2 py-0.5 text-right tabular-nums ${signo(h.dart)}`}>{num(h.dart)}</td>
            <td className={`px-2 py-0.5 text-right tabular-nums font-medium ${signo(h.pl_mxn)}`}>
              {h.pl_mxn == null ? "sin MTR" : mxn(h.pl_mxn)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
